import websiteIcon from 'public/static/images/vdp/website-icon.svg';
import phoneIcon from 'public/static/images/vdp/call-icon.svg';
import emailIcon from 'public/static/images/vdp/email-icon.svg';
import shareIcon from 'public/static/images/vdp/dealer-share-icon.svg';
import { useTranslation } from 'react-i18next';
import useEvents from 'lib/hooks/useEvents';
import tapEvent from 'lib/Tracking/Vdp/SellerSnapshot/TapEvent';
import { ShareActions } from 'lib/Tracking/Vdp/VehicleSnapshot/TapEvent';
import VdpEventContext from 'contexts/VdpEventContext';
import { useContext } from 'react';
import formatRawPhoneNumber from 'lib/formatRawPhoneNumber';
import PhoneLinkOnlyClickableOnMobile from 'components/PhoneLinkOnlyClickableOnMobile';
import ShareDropdown from 'components/Shared/ShareDropdown';
import GoogleAnalytics, { ECOMMERCE_REVENUE } from 'lib/GoogleAnalytics';
import { actionCss } from './styles';

interface Props {
  phoneNumber: string | null;
  openLeadForm: () => void;
  website: string | null;
  className?: string;
  make: string;
  model: string;
  year: number;
  trim: string | null;
  vin: string;
}

export default function Actions({
  className,
  phoneNumber,
  openLeadForm,
  website,
  make,
  model,
  year,
  trim,
  vin,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const logger = useEvents();
  const { trackingParams } = useContext(VdpEventContext);
  const title = `${year} ${make} ${model} ${trim}`;

  function logSellerSnapshotTap(action: string): void {
    logger.log(tapEvent({ action, trackingParams }));
  }

  let formattedPhoneNumber: string | undefined;

  if (phoneNumber) {
    formattedPhoneNumber = formatRawPhoneNumber(phoneNumber);
  }

  return (
    <ul className={`actions ${className}`}>
      {formattedPhoneNumber && phoneNumber && (
        <li className="actions-list-item">
          <span className="action-button">
            <PhoneLinkOnlyClickableOnMobile
              phoneNumber={phoneNumber}
              onClick={(): void => {
                logSellerSnapshotTap('call');
              }}
            >
              <img className="icon phone-icon" src={phoneIcon} alt={t('vdp.actions.call')} />
              <span className="title">{formattedPhoneNumber}</span>
            </PhoneLinkOnlyClickableOnMobile>
          </span>
        </li>
      )}
      <li className="actions-list-item">
        <button
          data-testid="seller-email-button"
          onClick={(): void => {
            logSellerSnapshotTap('email');
            openLeadForm();
          }}
          className="action-button"
        >
          <img className="icon email-icon" src={emailIcon} alt={t('vdp.actions.email')} />
          <span className="title">{t('vdp.actions.email')}</span>
        </button>
      </li>
      {website && (
        <li className="action actions-list-item">
          <a
            data-testid="dealer-website"
            href={website}
            target="_blank"
            aria-label={t('accessibility.newWindow')}
            className="action-button"
            onClick={(): void => {
              logSellerSnapshotTap('website');
              GoogleAnalytics.ecommerce({
                id: vin,
                revenue: ECOMMERCE_REVENUE.clickOff,
                location: `listings/${vin}`,
              });
            }}
          >
            <img className="icon website-icon" src={websiteIcon} alt={t('vdp.website')} />
            <span className="title">{t('vdp.website')}</span>
          </a>
        </li>
      )}
      <li className="share-container actions-list-item" data-testid="share-container">
        <button
          onClick={(): void => {
            logSellerSnapshotTap('share');
            // eslint-disable-next-line no-unused-expressions
            window.navigator.share?.({
              url: window.location.origin,
            });
          }}
          data-testid="share-button"
          className={`share-button action-button`}
        >
          <img className="icon share-icon" src={shareIcon} alt={t('vdp.actions.share')} />
          <span className="title">{t('vdp.actions.share')}</span>
        </button>
        <div className="share-dropdown" data-testid="share-dropdown">
          <ShareDropdown
            url={`https://www.autolist.com/listings/${vin}`}
            shareText={'Check out this vehicle on Autolist!'}
            shareSubject={title}
            onEmailClick={(): void => {
              logSellerSnapshotTap(ShareActions.email);
            }}
            onFacebookClick={(): void => {
              logSellerSnapshotTap(ShareActions.fb);
            }}
            onTwitterClick={(): void => {
              logSellerSnapshotTap(ShareActions.twitter);
            }}
            onCopyClick={(): void => {
              logSellerSnapshotTap(ShareActions.copy);
            }}
          />
        </div>
      </li>
      <style jsx>{actionCss}</style>{' '}
    </ul>
  );
}
